/**
 * Utility functions for Safetrax WebConfig
 */

/**
 * Get config byte count for firmware version
 * @param {string} version - Firmware version (e.g., "1.50", "1.55")
 * @returns {number} Total byte count (including checksum)
 */
export function getConfigByteCount(version) {
  if (!version) return 57; // Default to v1.55 behavior
  const versionNum = parseFloat(version);
  return versionNum < 1.55 ? 49 : 57;
}

/**
 * Get checksum index (position) for firmware version
 * @param {string} version - Firmware version (e.g., "1.50", "1.55")
 * @returns {number} Checksum byte index
 */
export function getChecksumIndex(version) {
  if (!version) return 56; // Default to v1.55 behavior
  const versionNum = parseFloat(version);
  return versionNum < 1.55 ? 48 : 56;
}

/**
 * Get parameter byte count (bytes before checksum) for firmware version
 * @param {string} version - Firmware version (e.g., "1.50", "1.55")
 * @returns {number} Parameter byte count (excluding checksum)
 */
export function getParamByteCount(version) {
  return getChecksumIndex(version);
}

/**
 * Infer firmware version from config byte count
 * @param {number} byteCount - Config byte count
 * @returns {string|null} Firmware version or null if unknown
 */
export function inferVersionFromByteCount(byteCount) {
  if (byteCount === 49) return '1.50';
  if (byteCount === 57) return '1.55';
  return null;
}

/**
 * Calculate checksum for config bytes
 * Checksum = -sum(param bytes) & 0xFF
 * @param {Uint8Array} bytes - Config byte array
 * @param {string} version - Firmware version (optional, defaults to v1.55)
 * @returns {number} Checksum byte
 */
export function calculateChecksum(bytes, version = null) {
  const paramCount = getParamByteCount(version);
  
  if (bytes.length < paramCount) {
    throw new Error(`Invalid config: must have at least ${paramCount} bytes for version ${version || '1.55'}`);
  }
  
  const sum = bytes.slice(0, paramCount).reduce((acc, byte) => acc + byte, 0);
  return (-sum) & 0xFF;
}

/**
 * Validate config checksum
 * @param {Uint8Array} bytes - Config byte array
 * @param {string} version - Firmware version (optional, defaults to v1.55)
 * @returns {{valid: boolean, computed: number, provided: number}}
 */
export function validateChecksum(bytes, version = null) {
  const expectedByteCount = getConfigByteCount(version);
  const checksumIndex = getChecksumIndex(version);
  
  if (bytes.length !== expectedByteCount) {
    throw new Error(`Invalid config length: expected ${expectedByteCount} bytes for version ${version || '1.55'}, got ${bytes.length}`);
  }
  
  const computed = calculateChecksum(bytes, version);
  const provided = bytes[checksumIndex];
  
  return {
    valid: computed === provided,
    computed,
    provided
  };
}

/**
 * Validate that two config byte arrays are identical and checksums are valid
 * @param {Uint8Array} expectedBytes - Bytes that were sent to device
 * @param {Uint8Array} actualBytes - Bytes read back from device
 * @param {string} version - Firmware version (optional, inferred from byte count)
 * @returns {{
 *   valid: boolean,
 *   lengthMatch: boolean,
 *   checksum: { expectedValid: boolean, actualValid: boolean },
 *   mismatchCount: number,
 *   mismatches: Array<{ index: number, expected: number, actual: number }>
 * }}
 */
export function validateConfigMatch(expectedBytes, actualBytes, version = null) {
  if (!(expectedBytes instanceof Uint8Array) || !(actualBytes instanceof Uint8Array)) {
    throw new Error('validateConfigMatch requires Uint8Array inputs');
  }

  if (!version) {
    version = inferVersionFromByteCount(expectedBytes.length) || inferVersionFromByteCount(actualBytes.length) || '1.55';
  }

  const expectedChecksum = validateChecksum(expectedBytes, version);
  const actualChecksum = validateChecksum(actualBytes, version);

  const lengthMatch = expectedBytes.length === actualBytes.length;
  const compareLength = Math.min(expectedBytes.length, actualBytes.length);
  const mismatches = [];

  for (let i = 0; i < compareLength; i++) {
    if (expectedBytes[i] !== actualBytes[i]) {
      mismatches.push({
        index: i,
        expected: expectedBytes[i],
        actual: actualBytes[i]
      });
    }
  }

  return {
    valid: lengthMatch && mismatches.length === 0 && expectedChecksum.valid && actualChecksum.valid,
    lengthMatch,
    checksum: {
      expectedValid: expectedChecksum.valid,
      actualValid: actualChecksum.valid
    },
    mismatchCount: mismatches.length,
    mismatches
  };
}

/**
 * Convert device angle units to display degrees
 * Device stores angles as (degrees × 2)
 * @param {number} deviceValue - Device angle value
 * @returns {number} Angle in degrees
 */
export function deviceToDisplayAngle(deviceValue) {
  return deviceValue / 2;
}

/**
 * Convert display degrees to device angle units
 * @param {number} displayDegrees - Angle in degrees
 * @returns {number} Device angle value (degrees × 2)
 */
export function displayToDeviceAngle(displayDegrees) {
  return Math.round(displayDegrees * 2);
}

/**
 * Convert hex string to byte array
 * @param {string} hexString - Hex string (e.g., "18322D14")
 * @returns {Uint8Array} Byte array
 */
export function hexToBytes(hexString) {
  // Remove whitespace and validate
  const cleaned = hexString.replace(/\s/g, '');
  
  if (cleaned.length % 2 !== 0) {
    throw new Error('Invalid hex string: must have even number of characters');
  }
  
  if (!/^[0-9A-Fa-f]*$/.test(cleaned)) {
    throw new Error('Invalid hex string: contains non-hex characters');
  }
  
  const bytes = new Uint8Array(cleaned.length / 2);
  
  for (let i = 0; i < cleaned.length; i += 2) {
    bytes[i / 2] = parseInt(cleaned.substr(i, 2), 16);
  }
  
  return bytes;
}

/**
 * Convert byte array to hex string
 * @param {Uint8Array|Array} bytes - Byte array
 * @param {boolean} uppercase - Whether to use uppercase hex (default: true)
 * @returns {string} Hex string
 */
export function bytesToHex(bytes, uppercase = true) {
  const hex = Array.from(bytes)
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');
  
  return uppercase ? hex.toUpperCase() : hex;
}

/**
 * Format hex string with spaces every 2 characters
 * @param {string} hexString - Hex string
 * @returns {string} Formatted hex string
 */
export function formatHex(hexString) {
  return hexString.match(/.{1,2}/g)?.join(' ') || hexString;
}

/**
 * Parse config file text format (hex-encoded)
 * @param {string} text - Config file content
 * @returns {{name: string, bytes: Uint8Array, config: object}}
 */
export function parseConfigFile(text) {
  const lines = text.split('\n').map(line => line.trim()).filter(line => line);
  
  if (lines.length < 4) {
    throw new Error('Invalid config file format: expected at least 4 lines');
  }
  
  // Line 0: Comment
  // Line 1: Config name
  const name = lines[1].replace(/^\*\s*/, '').trim();
  
  // Lines 2+: Hex data
  const hexData = lines.slice(2).join('').replace(/\s/g, '');

  if (hexData.length % 2 !== 0) {
    throw new Error(`Invalid hex data length: expected even char count, got ${hexData.length}`);
  }

  const allBytes = hexToBytes(hexData);

  // Support config files that may include calibration bytes appended after parameters:
  // - v1.50 core config: 49 bytes (checksum at index 48)
  // - v1.55+ core config: 57 bytes (checksum at index 56)
  // Prefer newer/longer core format if both are available.
  const candidates = [57, 49].filter(count => allBytes.length >= count);

  let bytes = null;
  let detectedVersion = null;
  let checksumError = null;

  for (const count of candidates) {
    const candidate = allBytes.slice(0, count);
    const version = inferVersionFromByteCount(count);

    try {
      const checksumResult = validateChecksum(candidate, version);
      if (checksumResult.valid) {
        bytes = candidate;
        detectedVersion = version;
        checksumError = null;
        break;
      }

      checksumError = `Checksum mismatch: computed ${checksumResult.computed}, provided ${checksumResult.provided}`;
    } catch (err) {
      checksumError = err.message;
    }
  }

  if (!bytes) {
    const expected = '98 (49 bytes) or 114 (57 bytes)';
    const got = hexData.length;
    const extra = checksumError ? `; ${checksumError}` : '';
    throw new Error(`Invalid config payload length/checksum: expected ${expected} hex chars (optionally with trailing calibration bytes), got ${got}${extra}`);
  }
  
  // Parse config values
  const config = parseConfigBytes(bytes);
  
  return {
    name,
    bytes,
    config,
    version: detectedVersion
  };
}

/**
 * Parse config byte array into config object
 * @param {Uint8Array} bytes - 57-byte config array
 * @returns {object} Parsed config
 */
export function parseConfigBytes(bytes) {
  const checksumIndex = bytes.length === 49 ? 48 : 56;
  const xAxisForward = (bytes[0] & 0x20) !== 0;  // EE_FLAGS bit 5 (EFLG_XFWD)
  
  // The meaning of ANGLEX and ANGLEY depends on orientation:
  // Y-axis forward (default): ANGLEX = roll, ANGLEY = pitch
  // X-axis forward: ANGLEX = pitch, ANGLEY = roll
  const angleX = deviceToDisplayAngle(bytes[14]);  // EE_WRO_ANGLEX
  const angleY = deviceToDisplayAngle(bytes[15]);  // EE_WRO_ANGLEY
  
  return {
    flags: bytes[0],
    rollWarning: xAxisForward ? angleY : angleX,
    pitchWarning: xAxisForward ? angleX : angleY,
    alarmAngle: deviceToDisplayAngle(bytes[18]),       // EE_ARO_ANGLE (was incorrectly bytes[17])
    buzzerOnTime: bytes[26],                           // EE_BUZ_W_ON_TIME
    buzzerEnabled: bytes[26] > 0,
    xAxisForward: xAxisForward,
    xAxisTestEnabled: (bytes[0] & 0x08) !== 0,         // EE_FLAGS bit 3
    yAxisTestEnabled: (bytes[0] & 0x10) !== 0,         // EE_FLAGS bit 4
    checksum: bytes[checksumIndex]
  };
}

/**
 * Serialize config file to text format
 * @param {string} name - Config name
 * @param {Uint8Array} bytes - Config byte array (version-specific)
 * @param {string} version - Firmware version (optional, inferred from byte count)
 * @returns {string} Config file text
 */
export function serializeConfigFile(name, bytes, version = null) {
  // Infer version from byte count if not provided
  if (!version) {
    version = bytes.length === 49 ? '1.50' : '1.55';
  }
  
  const expectedByteCount = getConfigByteCount(version);
  const checksumIndex = getChecksumIndex(version);
  
  if (bytes.length !== expectedByteCount) {
    throw new Error(`Invalid config length for v${version}: expected ${expectedByteCount} bytes, got ${bytes.length}`);
  }
  
  // Ensure checksum is correct
  bytes[checksumIndex] = calculateChecksum(bytes, version);
  
  const hex = bytesToHex(bytes);
  
  // Split into 4 lines
  const lines = [
    '* ROS Config Data',
    `* ${name}`,
    hex.substr(0, 32),
    hex.substr(32, 32),
    hex.substr(64, 32),
    hex.substr(96)
  ];
  
  return lines.join('\n') + '\n';
}

/**
 * Format timestamp for console log
 * @param {Date} date - Date object (default: now)
 * @returns {string} Formatted timestamp [HH:MM:SS]
 */
export function formatTimestamp(date = new Date()) {
  return `[${date.toLocaleTimeString()}]`;
}

/**
 * Delay utility
 * @param {number} ms - Milliseconds to delay
 * @returns {Promise<void>}
 */
export function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Parse sensor data response (S command)
 * Format from Arduino (legacy firmware):
 * - S followed by 12 hex words (4 digits each)
 * - Words 0-2: gyro X/Y/Z
 * - Words 3-5: accel X/Y/Z
 * - Words 6-9: filtered data
 * - Words 10-11: calculated angles (pitch/roll)
 * @param {string} response - Hex response string (e.g., "S0001 0002 0003 ... 0ABC")
 * @returns {object} Parsed sensor data
 */
export function parseSensorData(response) {
  try {
    // The response starts with 'S' followed by hex words
    // Remove the 'S' prefix and any non-hex content
    const cleaned = response.replace(/^S/i, '').trim();
    
    // Split by whitespace and parse hex words (each is 4 hex digits)
    const hexWords = cleaned.split(/\s+/)
      .map(w => w.trim())
      .filter(w => /^[0-9A-Fa-f]{4}$/.test(w));
    
    if (hexWords.length < 12) {
      console.warn(`Incomplete sensor data: expected 12 words, got ${hexWords.length}`, response);
      return null;
    }
    
    // Convert hex words to signed int16 values (like legacy code)
    const values = hexWords.map(hexToSignedInt16);
    
    // Extract calculated angles from last 2 values
    // Legacy code: values.slice(-2).map(v => v / 256)
    // Arduino firmware stores roll in angleX (word 10) and pitch in angleY (word 11)
    // See ros.ino line 3627-3628: angleX = (short)(roll * 128); angleY = (short)(pitch * 128);
    const roll = values[10] / 256;   // angleX is actually roll
    const pitch = values[11] / 256;  // angleY is actually pitch
    
    return {
      pitch,              // degrees
      roll,               // degrees
      battery: 0,         // Battery voltage not included in S command
      swBattery: 0,       // Must use T command for battery data
      raw: {
        // Accel (values 0-2 in legacy, but actual order is gyro then accel)
        accelX: values[0],
        accelY: values[1],
        accelZ: values[2],
        // Gyro (values 3-5 in legacy)
        gyroX: values[3],
        gyroY: values[4],
        gyroZ: values[5],
        // Filtered/averaged values
        avg6: values[6],
        avg7: values[7],
        avg8: values[8],
        avg9: values[9],
        // Calculated angles (raw values before division)
        angleX: values[10],
        angleY: values[11]
      }
    };
  } catch (err) {
    console.error('Error parsing sensor data:', err, response);
    return null;
  }
}

/**
 * Convert hex string to signed 16-bit integer (matching legacy implementation)
 * @param {string} hex - 4-digit hex string
 * @returns {number} Signed 16-bit integer
 */
function hexToSignedInt16(hex) {
  let intValue = parseInt(hex, 16);
  // Convert to signed if needed
  if (intValue & 0x8000) {
    intValue -= 0x10000;
  }
  return intValue;
}

/**
 * Download file to browser
 * @param {string} filename - File name
 * @param {string} content - File content
 * @param {string} mimeType - MIME type (default: text/plain)
 */
export function downloadFile(filename, content, mimeType = 'text/plain') {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Read file from browser file input
 * @param {File} file - File object
 * @returns {Promise<string>} File content
 */
export function readFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e);
    reader.readAsText(file);
  });
}
